import type { TableValueFormat } from "./format-table-value"

export type PerformanceKpiId =
  | "cpa"
  | "roas"
  | "spend"
  | "creatives"
  | "thumbstop"
  | "stickRate"
  | "cpm"
  | "aov"
  | "ctr"
  | "cpc"
  | "cr"
  | "engagement"
  | "spendPerCreative"
  | "spendPerSticked"
  | "frequency"
  | "holdrate"
  | "conversions"
  | "custom"
  | "spendPct"
  | "creativesPct"
  | "ads"

/**
 * How the badge compares the creative to its peer-average baseline (Retool parity):
 * - `offset`: formula KPI — `(current - baseline) / baseline`, colored by `betterDirection`.
 * - `share`: count KPI — `current / baseline` (share %), neutral tone.
 * - `none`: value only, no badge.
 */
export type PerformanceComparison = "offset" | "share" | "none"

/** Favorable direction for `offset` KPIs (green when better). `neutral` for share/none. */
export type PerformanceBetterDirection = "high" | "low" | "neutral"

export interface PerformanceKpiCatalogEntry {
  id: PerformanceKpiId
  /** Maps to SQL column suffix (`current_*` / `baseline_*`). */
  sqlSuffix: string
  labelKey: string
  defaultLabel: string
  format: TableValueFormat
  comparison: PerformanceComparison
  betterDirection: PerformanceBetterDirection
}

/**
 * Fixed Figma order — 21 KPI cells. `comparison`/`betterDirection` mirror Retool's
 * `KPI_BETTER_DIRECTION` map. Quirks preserved intentionally: Engagement and Custom
 * are cost metrics but Retool colors them `high` (green when above the peer average).
 */
export const MY_BRAND_DRAWER_PERFORMANCE_CATALOG: PerformanceKpiCatalogEntry[] = [
  { id: "cpa", sqlSuffix: "cpa", labelKey: "drilldown.myBrandDrawer.performance.cpa", defaultLabel: "CPA", format: "currency", comparison: "offset", betterDirection: "low" },
  { id: "roas", sqlSuffix: "roas", labelKey: "drilldown.myBrandDrawer.performance.roas", defaultLabel: "ROAS", format: "ratio", comparison: "offset", betterDirection: "high" },
  { id: "spend", sqlSuffix: "spend", labelKey: "drilldown.myBrandDrawer.performance.spend", defaultLabel: "Spend", format: "currency", comparison: "share", betterDirection: "neutral" },
  { id: "creatives", sqlSuffix: "creatives", labelKey: "drilldown.myBrandDrawer.performance.creatives", defaultLabel: "Creatives", format: "number", comparison: "share", betterDirection: "neutral" },
  { id: "thumbstop", sqlSuffix: "thumbstop", labelKey: "drilldown.myBrandDrawer.performance.thumbstop", defaultLabel: "Thumbstop", format: "percent", comparison: "offset", betterDirection: "high" },
  { id: "stickRate", sqlSuffix: "stick_rate", labelKey: "drilldown.myBrandDrawer.performance.stickRate", defaultLabel: "Stick Rate", format: "percent", comparison: "offset", betterDirection: "high" },
  { id: "cpm", sqlSuffix: "cpm", labelKey: "drilldown.myBrandDrawer.performance.cpm", defaultLabel: "CPM", format: "currency", comparison: "offset", betterDirection: "low" },
  { id: "aov", sqlSuffix: "aov", labelKey: "drilldown.myBrandDrawer.performance.aov", defaultLabel: "AOV", format: "currency", comparison: "offset", betterDirection: "high" },
  { id: "ctr", sqlSuffix: "ctr", labelKey: "drilldown.myBrandDrawer.performance.ctr", defaultLabel: "CTR", format: "percent", comparison: "offset", betterDirection: "high" },
  { id: "cpc", sqlSuffix: "cpc", labelKey: "drilldown.myBrandDrawer.performance.cpc", defaultLabel: "CPC", format: "currency", comparison: "offset", betterDirection: "low" },
  { id: "cr", sqlSuffix: "cr", labelKey: "drilldown.myBrandDrawer.performance.cr", defaultLabel: "CR", format: "percent", comparison: "offset", betterDirection: "high" },
  // Quirk: Retool maps Engagement (Spend/clicks, a cost metric) to `high`.
  { id: "engagement", sqlSuffix: "engagement", labelKey: "drilldown.myBrandDrawer.performance.engagement", defaultLabel: "Engagement", format: "currency", comparison: "offset", betterDirection: "high" },
  { id: "spendPerCreative", sqlSuffix: "spend_per_creative", labelKey: "drilldown.myBrandDrawer.performance.spendPerCreative", defaultLabel: "Spend/Creative", format: "currency", comparison: "offset", betterDirection: "low" },
  { id: "spendPerSticked", sqlSuffix: "spend_per_sticked", labelKey: "drilldown.myBrandDrawer.performance.spendPerSticked", defaultLabel: "Spend/Sticked", format: "currency", comparison: "offset", betterDirection: "low" },
  { id: "frequency", sqlSuffix: "frequency", labelKey: "drilldown.myBrandDrawer.performance.frequency", defaultLabel: "Frequency", format: "number", comparison: "none", betterDirection: "neutral" },
  { id: "holdrate", sqlSuffix: "holdrate", labelKey: "drilldown.myBrandDrawer.performance.holdrate", defaultLabel: "Holdrate", format: "percent", comparison: "offset", betterDirection: "high" },
  { id: "conversions", sqlSuffix: "conversions", labelKey: "drilldown.myBrandDrawer.performance.conversions", defaultLabel: "Conversions", format: "number", comparison: "share", betterDirection: "neutral" },
  // Quirk: Retool maps Custom (cost-per-custom) to `neutral`, which its tone path treats as `high`.
  // The drawer cell overrides this with the tenant's Custom polarity when set (cellFromCatalogColumn).
  { id: "custom", sqlSuffix: "cost_per_custom", labelKey: "drilldown.myBrandDrawer.performance.custom", defaultLabel: "Custom", format: "currency", comparison: "offset", betterDirection: "high" },
  { id: "spendPct", sqlSuffix: "spend_pct", labelKey: "drilldown.myBrandDrawer.performance.spendPct", defaultLabel: "Spend %", format: "percent", comparison: "none", betterDirection: "neutral" },
  { id: "creativesPct", sqlSuffix: "creatives_pct", labelKey: "drilldown.myBrandDrawer.performance.creativesPct", defaultLabel: "Creatives %", format: "percent", comparison: "none", betterDirection: "neutral" },
  { id: "ads", sqlSuffix: "ads", labelKey: "drilldown.myBrandDrawer.performance.ads", defaultLabel: "Ads", format: "number", comparison: "share", betterDirection: "neutral" },
]

export const PERFORMANCE_GRID_SLOT_COUNT = 24
export const PERFORMANCE_EMPTY_SLOT_COUNT = 3

/**
 * Drawer KPI id → canonical `Kpi.name`.
 *
 * The performance drawer reads pre-aggregated `current_*` / `baseline_*` columns
 * for these 21 catalog names (Retool-parity badge math). Formula KPIs that are
 * not in this map compile against the raw `COLUMN_FIELDS` aggregates on the
 * same row. Every id must appear here — `my-brand-drawer-performance-fields.test.ts`
 * fails the build if one is missing, so a metric cannot be silently dropped when
 * the drawer converges on the user catalog.
 */
export const PERFORMANCE_KPI_NAME_BY_ID: Record<PerformanceKpiId, string> = {
  cpa: "CPA",
  roas: "ROAS",
  spend: "Spend",
  creatives: "Creatives",
  thumbstop: "Thumbstop",
  stickRate: "Stick Rate",
  cpm: "CPM",
  aov: "AOV",
  ctr: "CTR",
  cpc: "CPC",
  cr: "CR",
  engagement: "Engagement",
  spendPerCreative: "Spend/Creatives",
  spendPerSticked: "Spend/Sticked",
  frequency: "Frequency",
  holdrate: "Holdrate",
  conversions: "Conversions",
  custom: "Custom",
  spendPct: "Spend %",
  creativesPct: "Creatives %",
  ads: "Ads",
}

/** Canonical `Kpi.name` → the drawer entry describing its BigQuery columns. */
export const PERFORMANCE_ENTRY_BY_KPI_NAME: Record<string, PerformanceKpiCatalogEntry> =
  Object.fromEntries(
    MY_BRAND_DRAWER_PERFORMANCE_CATALOG.map((entry) => [
      PERFORMANCE_KPI_NAME_BY_ID[entry.id],
      entry,
    ]),
  )

/** SQL suffix → catalog entry. Formula tokens that match a Figma cell reuse its badge math. */
export const PERFORMANCE_ENTRY_BY_SQL_SUFFIX: Partial<
  Record<string, PerformanceKpiCatalogEntry>
> = Object.fromEntries(MY_BRAND_DRAWER_PERFORMANCE_CATALOG.map((entry) => [entry.sqlSuffix, entry]))
